import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { TreeService } from '@modules/policy/services/location/tree.service';

import { PolicyDataService } from '../../../services/shared/policy-data.service';
import { Router } from '@angular/router';
import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { PolicyService } from '@modules/policy/policy.service';
import { BasicDialogComponent } from '@modules/policy/shared/components/basic-dialog/basic-dialog.component';
import { AssetFetcherService } from '@modules/policy/services/shared/asset-fetcher.service';
import { BehaviorSubject } from 'rxjs';
import { RatingService } from '@modules/policy/services/rating/rating.service';
import { SyncedPremiumState } from '@shared/models/state.model';
import { PremiumSyncService } from '@modules/policy/services/shared/premium-sync.service';
import { CustomActionService } from '@core/services/custom-action.service';

@Component({
  selector: 'app-policy-header',
  templateUrl: './policy-header.component.html',
  styleUrls: ['./policy-header.component.scss']
})
export class PolicyHeaderComponent implements OnInit {
  private policyId;
  private lob: string;
  data;
  policyDescriptionTxt;
  numLocations;
  headerConfig;
  numUnderwritingRules: number;
  policyReadOnly: BehaviorSubject<boolean>;
  @ViewChild('sidenav', { static: false }) sidenav: MatSidenav;

  @Input()
  set headerData(data) {
    if (data) {
      this.data = data;
      this.lob = data.PolicyLineOfBusinessCd;
      this.TreeService.updateNumLocations(data.PolicyNumOfLocationsQty);
    }
  }

  constructor(
    private PolicyDataService: PolicyDataService,
    private PolicyService: PolicyService,
    private router: Router,
    public dialog: MatDialog,
    private TreeService: TreeService,
    private assetFetcherService: AssetFetcherService,
    private ratingService: RatingService,
    private premiumSyncService: PremiumSyncService,
    private customActionService: CustomActionService
  ) { }

  ngOnInit() {
    this.policyId = this.PolicyDataService.getPolicyID();
    this.policyReadOnly = this.PolicyDataService.policyReadOnly
    this.headerConfig = this.PolicyDataService.getHeaderConfig();
    this.numUnderwritingRules = 0;
    this.TreeService.numLocationsSubject.subscribe(numLoc => this.setNumLocations(numLoc));
    this.PolicyDataService.policyDescriptionSubject.subscribe(data => {
      this.policyDescriptionTxt = data;
    });
    this.PolicyDataService.numUnderWritingRules.subscribe(num => {
      this.numUnderwritingRules = num;
    });
    this.premiumSyncService?.updateSyncedPremiumState(null);
  }

  setNumLocations(value) {
    this.numLocations = value;
  }

  showDisclaimer(title:string) {
    this.assetFetcherService.getAssetFile({
      name: 'disclaimer-content.html',
      quoteId: this.policyId
    }).subscribe(
      (res) => {
        this.dialog.open(BasicDialogComponent, {
          data: {
            title,
            htmlContent: res.data,
            buttons: [
              {
                label: 'OK',
                action: true
              }
            ]
          }
        });
      });
  }

  close() {
    this.sidenav.close();
  }

  async handlePolicyAction() {
    const { workflow, ignoreFormValidation } = this.headerConfig.action;
    if (!ignoreFormValidation) {
      const invalidTabs = this.getInvalidTabNames()
      if (invalidTabs.length) {
        return this.dialog.open(BasicDialogComponent, {
          data: {
            title: 'Warning',
            message: `The following tabs are incorrectly filled out:\n\n${invalidTabs}`,
            onlyConfirm: true
          },
          width: '400px'
        });
      }
    }

    switch (workflow) {
      case 'post':
        this.ratingService.ratePolicy(this.policyId).toPromise().then(res => {
          if (res.Error) this.openErrorModal(res.Error)
          else this.openPostModal(res.QuoteKey);
        })
        break;
      case 'convert':
        await this.triggerSaveGuards()
        await this.customActionService.executeCustomAction(this.policyId, {}, '/policy/custom-action/convert').toPromise()
        window.location.reload()
        break;
      case 'cancel':
        await this.confirmAndCancelPolicy()
        break;
      default:
        this.router.navigate([`/policy/${this.policyId}/${this.lob}/rating`]);
    }
  }

  async confirmAndCancelPolicy() {
    const confirmed = await this.dialog.open(BasicDialogComponent, {
      data: {
        title: 'Cancel Policy',
        message: 'This will cancel the policy and update client details in the enterprise system. This action cannot be undone.\n\nDo you want to continue?',
        dialogPrefix: 'cancel-policy-confirm-modal'
      },
      width: '480px'
    }).afterClosed().toPromise();

    if (!confirmed) return;

    await this.triggerSaveGuards()
    const result = await this.customActionService.executeCustomAction(this.policyId, {}, '/policy/custom-action/cancel').toPromise()
    this.openCancelSuccessModal(result?.PolicyISIPolicyKeyID ?? this.data?.PolicyISIPolicyKeyID);
  }

  openCancelSuccessModal(policyKey: string) {
    this.dialog.open(BasicDialogComponent, {
      data: {
        title: 'Policy Cancelled',
        message: `Policy ${policyKey} was successfully cancelled.`,
        onlyConfirm: true,
        dialogPrefix: 'cancel-policy-success-modal'
      },
      width: '480px'
    }).afterClosed().subscribe((_) => {
      this.router.navigate(['/dashboard']);
    });
  }

  async triggerSaveGuards() {
    const defaultRoute = this.PolicyDataService.getPolicyConfig().defaultRoute.replace(/:policyId/ig, this.policyId);
    // Leave the current tab via a real blank route so its SaveGuard fires. Trimming defaultRoute
    // (e.g. /policy/:id/auto/client/:clientId) matched no route and briefly rendered the 404 page.
    const editorRoute = defaultRoute.split('/').slice(0, 4).join('/'); // /policy/:id/:lob

    await this.router.navigateByUrl(`${editorRoute}/save-transition`, { skipLocationChange: true });
    await this.router.navigateByUrl(defaultRoute)
  }

  getInvalidTabNames(): string {
    const labelsMap = this.PolicyService.mapTabNameToLabel()
    const tabForms = this.PolicyService.getTabForms();
    const invalidTabs = []

    for (let tab in tabForms) {
      if (tabForms[tab].status === 'INVALID') {
        invalidTabs.push(`- ${labelsMap[tab]}`)
      }
    }

    return invalidTabs.join('\n');
  }

  getActionLabel() {
    return this.headerConfig.action.label;
  }

  openPostModal(quoteKey: string) {
    this.dialog.open(BasicDialogComponent, {
      data: {
        title: 'Quote Posted',
        message: `Comparative Quote successfully pushed to enterprise. 
        QuoteKey: ${quoteKey}`,
        onlyConfirm: true
      },
      width: '400px'
    }).afterClosed().toPromise().then(res => {
      this.router.navigate(['/dashboard'])
    });
  }

  openErrorModal({ Message, Details }: any = {}) {
    if (!Message || !Details) return
    this.dialog.open(BasicDialogComponent, {
      data: {
        title: 'Error',
        message: Message,
        details: Details,
        onlyConfirm: true,
        showDetails: Boolean(Details)
      },
      width: '400px'
    })
  }

  handleRenderer(displayItem) {
    const attributes = { 
      label: displayItem.label,
      action: null
    }

    switch (displayItem.renderer) {
      case 'locations':
        attributes.label = `${displayItem.label}: ${this.numLocations}`
        break
      case 'disclaimer':
        attributes.label = displayItem.label || 'Binding Authority';
        attributes.action = this.showDisclaimer.bind(this, attributes.label)
    }
    return attributes
  }

  getPremiumLabel({ premium, Error }: SyncedPremiumState) {
    if (Error) return 'Premium not available'
    return premium != null ? `Premium: $${premium}` : 'Calculating Premium'
  }

  async saveAndSyncPremium() {
    const buttonElement = document.querySelector('#form-save') as HTMLButtonElement;
    buttonElement?.click()
    this.premiumSyncService?.syncPremium()
  }

  ngOnDestroy() {
    this.PolicyDataService.updatePolicyDescription(null);
    this.premiumSyncService?.updateSyncedPremiumState(null);
  }
}
